// Lead Generation System — the shared FOUR-track action bar.
//
// Four INDEPENDENT two-stage toggle buttons, all visible at once, advanceable
// in ANY order (a broker can send a CIM before an NDA). Each button flips to
// its second stage on click and POSTs the matching endpoint; the row/detail is
// updated from the returned PreleadDetailDto and paying milestones rack the
// week's earnings. Reverts + terminal branches live in the "…" OverflowMenu;
// every revert writes an offsetting KPI row server-side. Used by BOTH the
// production-line list row AND the in-page detail view.
//
// Phoenix Capital is the Loan Administrator — the generator is sourcing and
// qualifying business-for-sale contacts, not brokering.

import { useRef, useState } from "react";
import { CheckCircle2 } from "lucide-react";
import {
  Button,
  Dropzone,
  OverflowMenu,
  StatusBadge,
  UploadFileRow,
  useSubmittingMutation,
  type UploadFileState,
} from "@/design-system/components/ui";
import {
  leadGeneratorApi,
  type PreleadCimStatus,
  type PreleadDetailDto,
  type PreleadInterestStatus,
  type PreleadNdaStatus,
  type PreleadOutreachStatus,
  type PreleadStatus,
} from "../../api/leadGenerator";
import { isEscalated, isTerminalClosed } from "./preleadStatus";

const MAX_FILE_BYTES = 25 * 1024 * 1024;

/** The track sub-set both PreleadSummaryDto and PreleadDetailDto carry. */
export interface PreleadTrackFields {
  id: string;
  status: PreleadStatus;
  outreachStatus: PreleadOutreachStatus;
  interestStatus: PreleadInterestStatus;
  ndaStatus: PreleadNdaStatus;
  cimStatus: PreleadCimStatus;
  escalatedToLeadId: string | null;
}

type Advance = {
  label: string;
  op: (id: string) => Promise<PreleadDetailDto>;
  /** Paying milestone — bump the racking chip after a successful click. */
  rack: boolean;
  /** The interest track's escalate step returns a lead id, not a prelead. */
  escalate?: boolean;
};

type Revert = { label: string; op: (id: string) => Promise<PreleadDetailDto> };

interface TrackView {
  key: string;
  /** Label of the current completed stage (shown when fully done / frozen). */
  chipLabel: string | null;
  /** The next-stage advance, or null when the track is fully done. */
  advance: Advance | null;
  /** Undo the current stage (populated only for a reached milestone). */
  revert: Revert | null;
}

const api = leadGeneratorApi;

function outreachTrack(s: PreleadOutreachStatus): TrackView {
  switch (s) {
    case "None":
      return {
        key: "outreach",
        chipLabel: null,
        advance: { label: "Message sent", op: api.messageSent, rack: true },
        revert: null,
      };
    case "MessageSent":
      return {
        key: "outreach",
        chipLabel: "Message sent",
        advance: { label: "Response received", op: api.responseReceived, rack: false },
        revert: { label: "Revert message sent", op: api.revertMessageSent },
      };
    case "ResponseReceived":
      return {
        key: "outreach",
        chipLabel: "Response received",
        advance: null,
        revert: { label: "Revert response received", op: api.revertResponseReceived },
      };
  }
}

function ndaTrack(s: PreleadNdaStatus): TrackView {
  switch (s) {
    case "NotRequested":
      return {
        key: "nda",
        chipLabel: null,
        advance: { label: "Request NDA", op: api.requestNda, rack: false },
        revert: null,
      };
    case "Requested":
      return {
        key: "nda",
        chipLabel: "NDA requested",
        advance: { label: "NDA completed & sent", op: api.completeNda, rack: true },
        revert: { label: "Revert NDA request", op: api.revertNdaRequested },
      };
    case "Completed":
      return {
        key: "nda",
        chipLabel: "NDA complete",
        advance: null,
        revert: { label: "Revert NDA completed", op: api.revertNdaCompleted },
      };
  }
}

function cimTrack(s: PreleadCimStatus): TrackView {
  switch (s) {
    case "NotRequested":
      return {
        key: "cim",
        chipLabel: null,
        advance: { label: "Request CIM", op: api.requestCim, rack: false },
        revert: null,
      };
    case "Requested":
      return {
        key: "cim",
        chipLabel: "CIM requested",
        advance: { label: "CIM completed & sent", op: api.receiveCim, rack: true },
        revert: { label: "Revert CIM request", op: api.revertCimRequested },
      };
    case "Received":
      return {
        key: "cim",
        chipLabel: "CIM received",
        advance: null,
        revert: { label: "Revert CIM received", op: api.revertCimReceived },
      };
  }
}

function interestTrack(s: PreleadInterestStatus, escalated: boolean): TrackView {
  if (escalated) {
    return { key: "interest", chipLabel: "Escalated", advance: null, revert: null };
  }
  if (s === "None") {
    return {
      key: "interest",
      chipLabel: null,
      advance: { label: "Mark interested", op: api.markInterested, rack: false },
      revert: null,
    };
  }
  return {
    key: "interest",
    chipLabel: "Interested",
    // Escalate is the interest track's SECOND stage (special: returns a lead id).
    advance: { label: "Escalate to lead", op: api.markInterested, rack: true, escalate: true },
    revert: { label: "Revert interested", op: api.revertInterested },
  };
}

function DoneChip({ label }: { label: string }) {
  return (
    <StatusBadge tone="ok" className="py-space-1">
      <CheckCircle2 size={12} aria-hidden /> {label}
    </StatusBadge>
  );
}

export interface PreleadActionButtonsProps {
  prelead: PreleadTrackFields;
  /** Fresh detail after a track advance / revert / terminal close. */
  onUpdated: (detail: PreleadDetailDto) => void;
  /** Escalation returns the new lead id (not a prelead). */
  onEscalated: (leadId: string) => void;
  /** Rack the week's earnings after a paying milestone. */
  onRack?: () => void;
  /** Show the optional signed-NDA / CIM file drops (detail view). */
  enableFileDrops?: boolean;
  /** Re-fetch after an NDA/CIM upload attaches a document. */
  onUploaded?: () => void;
  size?: "sm" | "md";
}

export function PreleadActionButtons({
  prelead,
  onUpdated,
  onEscalated,
  onRack,
  enableFileDrops = false,
  onUploaded,
  size = "sm",
}: PreleadActionButtonsProps) {
  const act = useSubmittingMutation(
    async (job: { op: (id: string) => Promise<PreleadDetailDto>; rack: boolean }) => {
      const detail = await job.op(prelead.id);
      return { detail, rack: job.rack };
    },
    {
      onSuccess: ({ detail, rack }) => {
        onUpdated(detail);
        if (rack) onRack?.();
      },
    },
  );

  const esc = useSubmittingMutation(() => api.escalate(prelead.id), {
    onSuccess: (res) => {
      onEscalated(res.leadId);
      onRack?.();
    },
  });

  const busy = act.pending || esc.pending;
  const terminalClosed = isTerminalClosed(prelead.status);
  const escalated = isEscalated(prelead.status, prelead.escalatedToLeadId);
  const live = !terminalClosed && !escalated;

  // Terminal-closed rows are history — the headline badge conveys the state.
  if (terminalClosed) return null;

  const tracks: TrackView[] = [
    outreachTrack(prelead.outreachStatus),
    ndaTrack(prelead.ndaStatus),
    cimTrack(prelead.cimStatus),
    interestTrack(prelead.interestStatus, escalated),
  ];

  const menuItems = live
    ? [
        ...tracks
          .filter((t) => t.revert)
          .map((t) => ({
            label: t.revert!.label,
            onSelect: () => void act.submit({ op: t.revert!.op, rack: false }),
          })),
        {
          label: "Bad fit",
          confirm: "Close as bad fit?",
          onSelect: () => void act.submit({ op: api.badFit, rack: false }),
        },
        {
          label: "Duplicate",
          confirm: "Close as duplicate?",
          onSelect: () => void act.submit({ op: api.duplicate, rack: false }),
        },
        {
          label: "No response",
          confirm: "Close as no response?",
          onSelect: () => void act.submit({ op: api.noResponse, rack: false }),
        },
        {
          label: "Close",
          destructive: true,
          confirm: "Close this prelead?",
          onSelect: () => void act.submit({ op: api.close, rack: false }),
        },
      ]
    : [];

  const error = act.error ?? esc.error;
  const showNdaDrop = enableFileDrops && prelead.ndaStatus !== "NotRequested";
  const showCimDrop = enableFileDrops && prelead.cimStatus !== "NotRequested";

  return (
    <div className="space-y-space-2">
      <div className="flex flex-wrap items-center gap-space-2">
        {tracks.map((t) => {
          if (live && t.advance) {
            const advance = t.advance;
            return (
              <Button
                key={t.key}
                variant={advance.escalate ? "primary" : "secondary"}
                size={size}
                disabled={busy}
                onClick={() =>
                  advance.escalate
                    ? void esc.submit()
                    : void act.submit({ op: advance.op, rack: advance.rack })
                }
                data-testid={`leadgen-track-${t.key}-${prelead.id}`}
              >
                {advance.label}
              </Button>
            );
          }
          if (t.chipLabel) {
            return <DoneChip key={t.key} label={t.chipLabel} />;
          }
          return null;
        })}

        {menuItems.length > 0 && (
          <OverflowActions
            items={menuItems}
            testId={`leadgen-menu-${prelead.id}`}
            disabled={busy}
          />
        )}
      </div>

      {(showNdaDrop || showCimDrop) && (
        <div className="flex flex-wrap gap-space-3">
          {showNdaDrop && (
            <FileDropRow
              label="signed NDA"
              testId={`leadgen-nda-${prelead.id}`}
              upload={(file) => api.uploadNda(prelead.id, file)}
              onUploaded={onUploaded}
            />
          )}
          {showCimDrop && (
            <FileDropRow
              label="CIM"
              testId={`leadgen-cim-${prelead.id}`}
              upload={(file) => api.uploadCim(prelead.id, file)}
              onUploaded={onUploaded}
            />
          )}
        </div>
      )}

      {error != null && (
        <p className="text-caption text-status-error m-0" role="alert">
          {error instanceof Error ? error.message : "That didn't go through. Try again."}
        </p>
      )}
    </div>
  );
}

// The shared OverflowMenu has no disabled prop, so we render an inert
// placeholder while a mutation is in flight (prevents a mid-flight revert).
function OverflowActions({
  items,
  testId,
  disabled,
}: {
  items: { label: string; onSelect: () => void; confirm?: string; destructive?: boolean }[];
  testId: string;
  disabled: boolean;
}) {
  if (disabled) {
    return (
      <span
        className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-border-light text-gray-mid opacity-40"
        aria-hidden
      >
        …
      </span>
    );
  }
  return <OverflowMenu items={items} label="More actions" data-testid={testId} />;
}

// ── Signed-file drop ─────────────────────────────────────────────────────────

function FileDropRow({
  label,
  testId,
  upload,
  onUploaded,
}: {
  label: string;
  testId: string;
  upload: (file: File) => Promise<unknown>;
  onUploaded?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [state, setState] = useState<UploadFileState>("ready");
  const [message, setMessage] = useState<string | null>(null);
  const inFlight = useRef(false);

  const onFiles = async (files: File[]) => {
    const picked = files[0];
    if (!picked || inFlight.current) return;
    inFlight.current = true;
    setFile(picked);
    setState("uploading");
    setMessage(null);
    try {
      await upload(picked);
      setState("done");
      setMessage("Attached");
      onUploaded?.();
    } catch (err) {
      setState("error");
      setMessage(err instanceof Error ? err.message : "Upload failed");
    } finally {
      inFlight.current = false;
    }
  };

  if (!open) {
    return (
      <Button variant="text" size="sm" onClick={() => setOpen(true)} data-testid={`${testId}-open`}>
        Attach {label} (optional)
      </Button>
    );
  }

  return (
    <div className="min-w-[220px] flex-1 space-y-space-2" data-testid={testId}>
      {file ? (
        <UploadFileRow
          name={file.name}
          sizeBytes={file.size}
          state={state}
          message={message ?? undefined}
          onRemove={
            state === "uploading"
              ? undefined
              : () => {
                  setFile(null);
                  setState("ready");
                  setMessage(null);
                }
          }
        />
      ) : (
        <Dropzone
          onFiles={(files) => void onFiles(files)}
          accept=".pdf,.doc,.docx"
          maxSizeBytes={MAX_FILE_BYTES}
          hint={`Drop the ${label} here (PDF or Word, up to 25 MB)`}
          data-testid={`${testId}-drop`}
        />
      )}
    </div>
  );
}
